/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { isNil, map, omit, cloneDeep, get } from 'lodash';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Icon, Form, Message } from 'semantic-ui-react';
import { format } from 'date-fns';

import { Modal, Button, FormLabel } from '../../../../../components';
import InvoiceFieldsList from './InvoiceFieldsList';
import InvoiceMessage from '../InvoiceMessage';
import PayButton from './PayButton';
import ArchiveButton from '../../../containers/details/ArchiveButton';
import { clearInvoiceProducts } from '../../../../../actions/invoice';
import { prepareFloatForServer } from '../../../../../lib/numberFormatter';

import { GET_SALON_QUERY } from '../../../containers/timetable/MasterTable';
import { GET_MASTER_APPOINTMENTS_QUERY } from '../../../../master-appointments/containers/queries';

import type { Appointment, Invoice } from '../../../../../type';

export const UPDATE_INVOICE_MUTATION = gql`
    mutation updateInvoice($input: UpdateInvoiceInput!, $lang: String!) {
        updateInvoice(input: $input) {
            invoice {
                id
                items {
                    id
                    type
                    entity {
                        ... on Service {
                            id
                            name(lang: $lang)
                        }
                        ... on Product {
                            id
                            name
                        }
                    }
                    sellerType
                    seller
                    quantity
                    price
                    paymentType
                    appointment {
                        id
                    }
                }
                totalPrice
                paid
            }
        }
    }
`;

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
    runOut: {
        id: 'pages.memberships.pay.runOut',
        defaultMessage: 'Membership is run out of uses',
    },
    membershipError: {
        id: 'pages.appointments.details.invoice.InvoiceFields.membershipError',
        defaultMessage: 'Only one use of membership is allowed',
    },
    membershipDoesNotExist: {
        id:
            'pages.appointments.details.invoice.InvoiceFields.membershipDoesNotExist',
        defaultMessage: 'Membership does not exist',
    },
});

type TotalPriceProps = {
    invoice: Invoice,
};

const TotalPriceWrapper = styled.div`
    margin-top: 1em;
`;

const TotalPrice = ({ invoice }: TotalPriceProps) => (
    <TotalPriceWrapper>
        <FormattedMessage
            id="pages.appointments.details.invoice.InvoiceForm.total_price"
            defaultMessage="Total price"
        />
        : &euro; {invoice.totalPrice.toFixed(2)}
    </TotalPriceWrapper>
);

const StyledForm = styled(Form)`
    & .ui.disabled.dropdown,
    & .ui.disabled.input {
        opacity: 0.7;
        input[disabled] {
            opacity: 0.7;
        }
    }
`;

const updateAppointment = (
    store,
    salon,
    startAt,
    clientId,
    masterId,
    invoiceId,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_SALON_QUERY,
            variables: {
                salon,
                showArchive,
                date: format(new Date(startAt), 'yyyy-MM-dd'),
                lang: currentLanguage,
            },
        });

        const masters = data.viewer.salon.masters.nodes || [];
        const nextMasters = masters.map(master => {
            const appointments = master.appointments || [];
            const nextAppointments = appointments.map(appointment => {
                let nextClient = appointment.client;
                if (appointment.client && appointment.client.id === clientId) {
                    const stats = appointment.client.stats || {};
                    nextClient = {
                        ...appointment.client,
                        stats: {
                            ...stats,
                            failedAppointments:
                                (stats.failedAppointments || 0) - 1,
                            successAppointments:
                                (stats.successAppointments || 0) + 1,
                        },
                    };
                }

                let nextInvoice = appointment.invoice;
                if (
                    appointment.invoice &&
                    appointment.invoice.id === invoiceId
                ) {
                    nextInvoice = {
                        ...appointment.invoice,
                        paid: true,
                    };
                }

                if (
                    nextClient !== appointment.client ||
                    nextInvoice !== appointment.invoice
                ) {
                    return {
                        ...appointment,
                        client: nextClient,
                        invoice: nextInvoice,
                    };
                }
                return appointment;
            });

            return {
                ...master,
                appointments: nextAppointments,
            };
        });

        store.writeQuery({
            query: GET_SALON_QUERY,
            variables: {
                salon,
                showArchive,
                date: format(new Date(startAt), 'yyyy-MM-dd'),
                lang: currentLanguage,
            },
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        masters: {
                            ...data.viewer.salon.masters,
                            nodes: nextMasters,
                        },
                    },
                },
            },
        });
    } catch (e) {
        // Cache miss — query not yet loaded, safe to ignore
    }
};

const updateAppointmentOnMasterAppointment = (
    store,
    salon,
    fromDate,
    toDate,
    clientId,
    masterId,
    invoiceId,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables: {
                salon,
                master: masterId,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                withMaster: true,
                showArchive,
                lang: currentLanguage,
            },
        });

        const appointments = data.viewer.salon.master.appointments || [];
        const nextAppointments = appointments.map(appointment => {
            let nextClient = appointment.client;
            if (appointment.client && appointment.client.id === clientId) {
                const stats = appointment.client.stats || {};
                nextClient = {
                    ...appointment.client,
                    stats: {
                        ...stats,
                        failedAppointments: (stats.failedAppointments || 0) - 1,
                        successAppointments:
                            (stats.successAppointments || 0) + 1,
                    },
                };
            }

            let nextInvoice = appointment.invoice;
            if (appointment.invoice && appointment.invoice.id === invoiceId) {
                nextInvoice = {
                    ...appointment.invoice,
                    paid: true,
                };
            }

            if (
                nextClient !== appointment.client ||
                nextInvoice !== appointment.invoice
            ) {
                return {
                    ...appointment,
                    client: nextClient,
                    invoice: nextInvoice,
                };
            }
            return appointment;
        });

        store.writeQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables: {
                salon,
                master: masterId,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                withMaster: true,
                showArchive,
                lang: currentLanguage,
            },
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        master: {
                            ...data.viewer.salon.master,
                            appointments: nextAppointments,
                        },
                    },
                },
            },
        });
    } catch (e) {
        // Cache miss — query not yet loaded, safe to ignore
    }
};

type InvoiceFormProps = {
    invoice: Invoice,
    appointment: Appointment,
    onClose: Function,
    membership: ?Object,
    masterAppointments: ?boolean,
    fromDate: ?string,
    toDate: ?string,
    intl: Object,
    enableArchive: ?boolean,
};

function InvoiceForm({
    invoice,
    appointment,
    onClose,
    membership,
    masterAppointments,
    fromDate,
    toDate,
    intl,
    enableArchive,
}: InvoiceFormProps) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));
    const isSysadmin = useSelector(({ user }) => user.get('isSysadmin'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const dispatch = useDispatch();

    const paid = invoice ? invoice.paid : false;

    const methods = useForm({
        defaultValues: {
            paid: false,
            items: map(invoice.items, item => ({
                ...omit(item, ['__typename', 'appointment']),
                entity: item.entity.id,
                price: item.price.toFixed(2),
                paymentType: item.paymentType,
            })),
        },
    });

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, errors },
        setError,
        setValue,
    } = methods;

    // Clear invoice products on mount (matches legacy lifecycle behavior)
    useEffect(() => {
        dispatch(clearInvoiceProducts());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { fields, append, remove } = useFieldArray({ control, name: 'items' });

    const [updateInvoice] = useMutation(UPDATE_INVOICE_MUTATION);

    const onSubmit = async formData => {
        const invoiceData = { ...formData };
        const itemsWithFormattedPrice = map(invoiceData.items, item => {
            const clonedItem = cloneDeep(item);
            clonedItem.price = prepareFloatForServer(clonedItem.price);
            return clonedItem;
        });
        invoiceData.items = itemsWithFormattedPrice;

        try {
            await updateInvoice({
                variables: {
                    input: {
                        invoiceId: invoice.id,
                        membership: (membership && membership.id) || null,
                        ...invoiceData,
                    },
                    lang: currentLanguage,
                },
                update: store => {
                    if (formData.paid) {
                        if (masterAppointments) {
                            updateAppointmentOnMasterAppointment(
                                store,
                                salon,
                                fromDate,
                                toDate,
                                appointment.client.id,
                                appointment.master.id,
                                appointment.invoice.id,
                                showArchive,
                                currentLanguage,
                            );
                        } else {
                            if (appointment.master) {
                                updateAppointment(
                                    store,
                                    salon,
                                    appointment.startAt,
                                    appointment.client.id,
                                    appointment.master.id,
                                    appointment.invoice.id,
                                    showArchive,
                                    currentLanguage,
                                );
                            }
                        }
                    }
                },
            });
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];

            if (graphQLError && graphQLError.data) {
                if (
                    get(graphQLError, 'data.error') ===
                    'NEGATIVE_QUANTITY_OF_MEMBERSHIP_USES'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.runOut),
                    });
                } else if (graphQLError.data.type === 'membership') {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.membershipError),
                    });
                } else if (
                    get(graphQLError, 'data.error') === 'MEMBERSHIP_DOES_NOT_EXIST'
                ) {
                    setError('root', {
                        message: intl.formatMessage(
                            intlMessages.membershipDoesNotExist,
                        ),
                    });
                } else {
                    setError('root', {
                        message:
                            (graphQLError && graphQLError.message) ||
                            intl.formatMessage(intlMessages.defaultErrorMessage),
                    });
                }
            } else {
                setError('root', {
                    message: intl.formatMessage(intlMessages.defaultErrorMessage),
                });
            }
        }
    };

    const onPay = () => {
        setValue('paid', true);
        setTimeout(() => handleSubmit(onSubmit)(), 0);
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    {errors.root && (
                        <Message error>
                            <Icon name="warning sign" />
                            {errors.root.message}
                        </Message>
                    )}
                    {paid && <InvoiceMessage />}

                    <StyledForm onSubmit={handleSubmit(onSubmit)}>
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.appointments.details.invoice.InvoiceForm.invoice_items"
                                    defaultMessage="Invoice items"
                                />
                            </FormLabel>
                            <InvoiceFieldsList
                                fields={fields}
                                append={append}
                                remove={remove}
                                readOnly={paid}
                                paid={paid}
                                invoice={invoice}
                                membership={membership}
                            />
                        </Form.Field>
                    </StyledForm>

                    {!isNil(invoice) && <TotalPrice invoice={invoice} />}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={paid || isSubmitting}
                        loading={isSubmitting}
                        primary
                    >
                        <Icon name="checkmark" />
                        <FormattedMessage
                            id="pages.appointments.details.invoice.InvoiceForm.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.appointments.details.invoice.InvoiceForm.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>

                    {invoice && !paid && (
                        <PayButton
                            invoice={invoice}
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            onPay={onPay}
                        />
                    )}
                    {(enableArchive || isSysadmin || isOwner) && paid && (
                        <ArchiveButton
                            appointment={appointment}
                            onClose={onClose}
                            masterAppointments={masterAppointments}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    )}
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(InvoiceForm);
